import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import "./AdminWhatsApp.css";

function formatPhone(phone) {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 13 && digits.startsWith("55")) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12 && digits.startsWith("55")) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return phone;
}

function relativeTime(ts) {
  const now = Date.now();
  const then = typeof ts === "number" && ts < 1e12 ? ts * 1000 : new Date(ts).getTime();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return "agora";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`;
  if (diffSec < 172800) return "ontem";
  return new Date(then).toLocaleDateString("pt-BR");
}

function fullDateTime(ts) {
  const d = typeof ts === "number" && ts < 1e12 ? new Date(ts * 1000) : new Date(ts);
  return d.toLocaleString("pt-BR");
}

function parseBadges(text) {
  if (!text) return { cleanText: "", badges: [] };
  const badges = [];
  let clean = text;
  const templateMatch = text.match(/\[template:([^\]]+)\]/);
  if (templateMatch) {
    badges.push({ type: "template", value: templateMatch[1] });
    clean = clean.replace(templateMatch[0], "").trim();
  }
  if (text.includes("[audio]")) {
    badges.push({ type: "audio" });
    clean = clean.replace("[audio]", "").trim();
  }
  return { cleanText: clean, badges };
}

export default function AdminWhatsApp() {
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [phoneFilter, setPhoneFilter] = useState("");
  const [debouncedPhone, setDebouncedPhone] = useState("");
  const [directionFilter, setDirectionFilter] = useState("");

  const [selectedPhone, setSelectedPhone] = useState(null);
  const [expandedMessage, setExpandedMessage] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [toast, setToast] = useState(null);

  const isPausedRef = useRef(false);
  const idleTimerRef = useRef(null);

  // Debounce phone filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPhone(phoneFilter);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [phoneFilter]);

  // Fetch messages
  const fetchMessages = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const data = await api.getWhatsAppMessages({
        phone: debouncedPhone || undefined,
        direction: directionFilter || undefined,
        page,
        page_size: pageSize,
      });
      setMessages(data.messages || []);
      setTotal(data.total || 0);
      setLastUpdated(new Date());
    } catch (err) {
      if (err.status === 401) {
        navigate("/login");
        return;
      }
      if (err.status === 403) {
        setError({ status: 403, message: "Acesso negado" });
      } else if (err.status === 503) {
        setError({ status: 503, message: "WhatsApp não configurado neste servidor" });
      } else if (isRefresh) {
        setToast("Falha ao atualizar mensagens");
        setTimeout(() => setToast(null), 5000);
      } else {
        setError({ status: 0, message: "Erro de conexão com o servidor" });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [debouncedPhone, directionFilter, page, pageSize, navigate]);

  // Initial fetch + refetch on filter/page change
  useEffect(() => {
    fetchMessages(false);
  }, [fetchMessages]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPausedRef.current) {
        fetchMessages(true);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  // Pause auto-refresh on interaction
  const pauseAutoRefresh = useCallback(() => {
    isPausedRef.current = true;
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      isPausedRef.current = false;
    }, 5000);
  }, []);

  // Derive phone list from messages
  const phoneList = useMemo(() => {
    const map = {};
    (messages || []).forEach((m) => {
      if (!map[m.phone]) {
        map[m.phone] = { phone: m.phone, count: 0, lastTs: 0, lastText: "" };
      }
      map[m.phone].count++;
      const ts = typeof m.ts === "number" && m.ts < 1e12 ? m.ts * 1000 : m.ts;
      if (ts > map[m.phone].lastTs) {
        map[m.phone].lastTs = ts;
        map[m.phone].lastText = m.text || "";
      }
    });
    return Object.values(map).sort((a, b) => b.lastTs - a.lastTs);
  }, [messages]);

  // Filter messages by selected phone (client-side)
  const displayMessages = useMemo(() => {
    if (!selectedPhone) return messages;
    return messages.filter((m) => m.phone === selectedPhone);
  }, [messages, selectedPhone]);

  const handleDirectionChange = (val) => {
    setDirectionFilter(val);
    setPage(1);
    pauseAutoRefresh();
  };

  const handlePageSizeChange = (val) => {
    setPageSize(Number(val));
    setPage(1);
  };

  const clearFilters = () => {
    setPhoneFilter("");
    setDebouncedPhone("");
    setDirectionFilter("");
    setSelectedPhone(null);
    setPage(1);
  };

  const handlePhoneSelect = (phone) => {
    setSelectedPhone(phone === selectedPhone ? null : phone);
    setExpandedMessage(null);
    pauseAutoRefresh();
  };

  const toggleExpand = (idx) => {
    setExpandedMessage(expandedMessage === idx ? null : idx);
    pauseAutoRefresh();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const totalPages = Math.ceil(total / pageSize);
  const showFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showTo = Math.min(page * pageSize, total);

  // 503 — show only banner
  if (error && error.status === 503) {
    return (
      <div className="wa-page">
        <div className="wa-header">
          <div className="wa-header-left">
            <h1>WhatsApp Messages</h1>
            <p>Log de mensagens das últimas 24 horas</p>
          </div>
        </div>
        <div className="wa-error-banner info">
          {"ℹ️"} {error.message}
        </div>
      </div>
    );
  }

  // 403 — access denied
  if (error && error.status === 403) {
    return (
      <div className="wa-page">
        <div className="wa-empty">
          <div className="wa-empty-icon">{"🚫"}</div>
          <h3>Acesso Negado</h3>
          <p>Você não tem permissão para acessar esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wa-page">
      {toast && <div className="wa-toast">{toast}</div>}

      {/* Header */}
      <div className="wa-header">
        <div className="wa-header-left">
          <h1>WhatsApp Messages</h1>
          <p>Log de mensagens das últimas 24 horas</p>
        </div>
        <div className="wa-header-right">
          {lastUpdated && (
            <div className="wa-refresh-info">
              <span className={`wa-refresh-dot ${isPausedRef.current ? "paused" : ""}`} />
              Atualizado {relativeTime(lastUpdated.getTime())}
            </div>
          )}
          <button
            className={`wa-btn-refresh ${refreshing ? "refreshing" : ""}`}
            onClick={() => fetchMessages(true)}
            disabled={refreshing}
          >
            {"↻"} {refreshing ? "..." : "Atualizar"}
          </button>
        </div>
      </div>

      {/* Error banner for network errors */}
      {error && error.status === 0 && (
        <div className="wa-error-banner danger">
          {"⚠"} {error.message}
        </div>
      )}

      {/* Filters */}
      <div className="wa-filters">
        <div className="wa-filter-input-wrap">
          <span className="wa-filter-icon">{"🔍"}</span>
          <input
            className="wa-filter-input"
            type="text"
            placeholder="Filtrar por telefone..."
            value={phoneFilter}
            onChange={(e) => setPhoneFilter(e.target.value)}
            onFocus={pauseAutoRefresh}
          />
        </div>
        <select
          className="wa-filter-select"
          value={directionFilter}
          onChange={(e) => handleDirectionChange(e.target.value)}
          onFocus={pauseAutoRefresh}
        >
          <option value="">Todas direções</option>
          <option value="inbound">{"↓"} Recebidas</option>
          <option value="outbound">{"↑"} Enviadas</option>
        </select>
        {(phoneFilter || directionFilter || selectedPhone) && (
          <button className="wa-filter-clear" onClick={clearFilters}>
            Limpar filtros
          </button>
        )}
      </div>

      {/* Main layout */}
      {loading ? (
        <div className="wa-layout">
          <div className="wa-sidebar">
            <div className="wa-skeleton-sidebar">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="wa-skeleton-phone" />
              ))}
            </div>
          </div>
          <div className="wa-main">
            <div className="wa-skeleton">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="wa-skeleton-row" />
              ))}
            </div>
          </div>
        </div>
      ) : messages.length === 0 ? (
        <div className="wa-layout">
          <div className="wa-main">
            <div className="wa-empty">
              {debouncedPhone || directionFilter ? (
                <>
                  <div className="wa-empty-icon">{"🔍"}</div>
                  <h3>Nenhuma mensagem encontrada</h3>
                  <p>Nenhuma mensagem corresponde aos seus filtros.</p>
                  <button className="wa-filter-clear" onClick={clearFilters}>
                    Limpar filtros
                  </button>
                </>
              ) : (
                <>
                  <div className="wa-empty-icon">{"💬"}</div>
                  <h3>Sem mensagens</h3>
                  <p>Nenhuma mensagem do WhatsApp nas últimas 24 horas.</p>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="wa-layout">
          {/* Phone sidebar */}
          <div className="wa-sidebar">
            <div className="wa-sidebar-header">Conversas ({phoneList.length})</div>
            <div
              className={`wa-phone-all ${!selectedPhone ? "active" : ""}`}
              onClick={() => { setSelectedPhone(null); setExpandedMessage(null); }}
            >
              {"📋"} Todas as mensagens
            </div>
            {phoneList.map((p) => (
              <div
                key={p.phone}
                className={`wa-phone-item ${selectedPhone === p.phone ? "active" : ""}`}
                onClick={() => handlePhoneSelect(p.phone)}
              >
                <div className="wa-phone-info">
                  <div className="wa-phone-number">{formatPhone(p.phone)}</div>
                  <div className="wa-phone-last-msg">
                    {p.lastText.length > 40 ? p.lastText.slice(0, 40) + "..." : p.lastText}
                  </div>
                </div>
                <div className="wa-phone-meta">
                  <span className="wa-phone-time">{relativeTime(p.lastTs)}</span>
                  <span className="wa-phone-count">{p.count}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Messages area */}
          <div className="wa-main">
            {selectedPhone ? (
              <>
                <div className="wa-main-header">
                  <span className="wa-phone-label">{formatPhone(selectedPhone)}</span>
                  <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
                    {displayMessages.length} mensagen{displayMessages.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div
                  className="wa-messages"
                  onMouseEnter={pauseAutoRefresh}
                >
                  {displayMessages.length === 0 ? (
                    <div className="wa-empty">
                      <div className="wa-empty-icon">{"💬"}</div>
                      <h3>Sem mensagens</h3>
                      <p>Nenhuma mensagem para este número com os filtros atuais.</p>
                    </div>
                  ) : (
                    [...displayMessages].reverse().map((m, idx) => {
                      const { cleanText, badges } = parseBadges(m.text);
                      const realIdx = displayMessages.length - 1 - idx;
                      return (
                        <div key={realIdx}>
                          <div
                            className={`wa-bubble-row ${m.direction}`}
                            onClick={() => toggleExpand(realIdx)}
                          >
                            <div className="wa-bubble">
                              {badges.length > 0 && (
                                <div style={{ marginBottom: 6 }}>
                                  {badges.map((b, bi) => (
                                    <span key={bi} className={`wa-badge ${b.type}`}>
                                      {b.type === "template" ? `📝 ${b.value}` : "🎤 audio"}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {cleanText}
                              <div className="wa-bubble-meta">
                                <span className={`wa-direction-icon ${m.direction}`}>
                                  {m.direction === "inbound" ? "↓" : "↑"}
                                </span>
                                <span title={fullDateTime(m.ts)}>{relativeTime(m.ts)}</span>
                              </div>
                            </div>
                          </div>
                          {expandedMessage === realIdx && (
                            <div className="wa-detail" style={{
                              maxWidth: "70%",
                              alignSelf: m.direction === "outbound" ? "flex-end" : "flex-start"
                            }}>
                              <div className="wa-detail-row">
                                <span className="wa-detail-label">Texto</span>
                                <span className="wa-detail-value">{m.text}</span>
                              </div>
                              <div className="wa-detail-row">
                                <span className="wa-detail-label">Direção</span>
                                <span className="wa-detail-value">{m.direction === "inbound" ? "Recebida" : "Enviada"}</span>
                              </div>
                              <div className="wa-detail-row">
                                <span className="wa-detail-label">Horário</span>
                                <span className="wa-detail-value">{fullDateTime(m.ts)}</span>
                              </div>
                              <div className="wa-detail-row">
                                <span className="wa-detail-label">Telefone</span>
                                <span
                                  className="wa-detail-value copyable"
                                  title="Clique para copiar"
                                  onClick={(e) => { e.stopPropagation(); copyToClipboard(m.phone); }}
                                >
                                  {m.phone}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="wa-main-header">
                  Todas as mensagens
                </div>
                <div style={{ overflowX: "auto", flex: 1 }} onMouseEnter={pauseAutoRefresh}>
                  <table className="wa-table">
                    <thead>
                      <tr>
                        <th className="cell-direction"></th>
                        <th>Telefone</th>
                        <th>Mensagem</th>
                        <th>Horário</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayMessages.map((m, idx) => {
                        const { cleanText, badges } = parseBadges(m.text);
                        return (
                          <tr key={idx} onClick={() => toggleExpand(idx)}>
                            <td className="cell-direction">
                              <span className={`wa-direction-icon ${m.direction}`}>
                                {m.direction === "inbound" ? "↓" : "↑"}
                              </span>
                            </td>
                            <td className="cell-phone">{formatPhone(m.phone)}</td>
                            <td className="cell-message">
                              {badges.map((b, bi) => (
                                <span key={bi} className={`wa-badge ${b.type}`} style={{ marginRight: 6 }}>
                                  {b.type === "template" ? `📝 ${b.value}` : "🎤 audio"}
                                </span>
                              ))}
                              <span title={m.text}>
                                {cleanText.length > 100 ? cleanText.slice(0, 100) + "..." : cleanText}
                              </span>
                            </td>
                            <td className="cell-time" title={fullDateTime(m.ts)}>
                              {relativeTime(m.ts)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {expandedMessage !== null && displayMessages[expandedMessage] && (
                    <div className="wa-detail" style={{ margin: "0 16px 16px" }}>
                      <div className="wa-detail-row">
                        <span className="wa-detail-label">Texto</span>
                        <span className="wa-detail-value">{displayMessages[expandedMessage].text}</span>
                      </div>
                      <div className="wa-detail-row">
                        <span className="wa-detail-label">Direção</span>
                        <span className="wa-detail-value">
                          {displayMessages[expandedMessage].direction === "inbound" ? "Recebida" : "Enviada"}
                        </span>
                      </div>
                      <div className="wa-detail-row">
                        <span className="wa-detail-label">Horário</span>
                        <span className="wa-detail-value">{fullDateTime(displayMessages[expandedMessage].ts)}</span>
                      </div>
                      <div className="wa-detail-row">
                        <span className="wa-detail-label">Telefone</span>
                        <span
                          className="wa-detail-value copyable"
                          title="Clique para copiar"
                          onClick={() => copyToClipboard(displayMessages[expandedMessage].phone)}
                        >
                          {displayMessages[expandedMessage].phone}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="wa-pagination">
                <span className="wa-pagination-info">
                  Mostrando {showFrom}–{showTo} de {total}
                </span>
                <div className="wa-pagination-controls">
                  <select
                    className="wa-page-size-select"
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <button
                    className="wa-pagination-btn"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    {"←"} Anterior
                  </button>
                  <span style={{ fontSize: 12, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                    {page}/{totalPages}
                  </span>
                  <button
                    className="wa-pagination-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próximo {"→"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
